/**
 * paymentFieldUtils.ts
 *
 * Utilities for payment field handling including amount calculation and customer data mapping.
 *
 * Key Functions:
 * - getPaymentAmount() - Calculate payment amount for a field
 * - getMappedPaymentValues() - Extract mapped customer data from form answers
 * - resolvePaymentField() - Find payment field in form structure
 */

import { Field } from "../../types/types";
import { FormVersionDetails } from "../../types/form-types";

// Runtime type for form version with converted fields
interface RuntimeFormVersionDetails extends Omit<FormVersionDetails, 'form_structure'> {
  form_structure: {
    pages: any[];
    fields: Field[];
    sections?: any[];
  };
}

/**
 * Calculate the payment amount for a field
 * Converts decimal amount to cents for payment processing
 * @param field - Payment field to process
 * @returns Amount in cents (multiply by 100), or 0 if no amount config
 */
export const getPaymentAmount = (field: Field): number => {
  if (!field.amountConfig) return 0;

  if (field.amountConfig.type === 'fixed' && field.amountConfig.amount) {
    // Amount is stored in decimal, convert to cents
    return field.amountConfig.amount * 100;
  }

  // For dynamic amounts, would need to evaluate expression
  // This is handled by parent component with access to all form values
  return 0;
};

/**
 * Extract customer data from form answers based on payment field mappings
 * @param field - Payment field with mapping configuration
 * @param answersMap - Map of form answers
 * @returns Object with mapped customer data (email, firstName, lastName, phone)
 */
export const getMappedPaymentValues = (
  field: Field | null | undefined,
  answersMap: Map<string, any>
): Record<string, string> => {
  if (!field?.paymentMapping) return {};

  const mapping = field.paymentMapping;
  const values: Record<string, string> = {};

  if (mapping.email && answersMap.has(mapping.email)) {
    values.email = answersMap.get(mapping.email);
  }
  if (mapping.firstName && answersMap.has(mapping.firstName)) {
    values.firstName = answersMap.get(mapping.firstName);
  }
  if (mapping.lastName && answersMap.has(mapping.lastName)) {
    values.lastName = answersMap.get(mapping.lastName);
  }
  if (mapping.phone && answersMap.has(mapping.phone)) {
    values.phone = answersMap.get(mapping.phone);
  }

  return values;
};

/**
 * Find and return the payment field from form structure
 * @param formVersion - Form version containing structure (accepts both API and runtime types)
 * @returns Payment field or undefined if not found
 */
export const resolvePaymentField = (
  formVersion: FormVersionDetails | RuntimeFormVersionDetails | null | undefined
): Field | undefined => {
  if (!formVersion?.form_structure?.fields) return undefined;

  // Search for payment field in the form structure
  const paymentField = (formVersion.form_structure.fields as Field[]).find(
    (f) => f.type === 'payment'
  );

  return paymentField;
};

/**
 * Check if a form is configured as a payment form
 * @param formVersion - Form version to check
 * @returns True if form is marked as payment form or has payment field
 */
export const isPaymentForm = (formVersion: FormVersionDetails | null | undefined): boolean => {
  if (!formVersion) return false;

  // Check explicit flag first
  if (formVersion.is_payment_form) return true;

  // Fallback: check if payment field exists
  return !!resolvePaymentField(formVersion);
};

/**
 * Build customer data object from mapped field values
 * Useful for pre-filling payment form with collected customer info
 * @param values - Mapped payment values from getMappedPaymentValues()
 * @returns Customer data object for payment processing
 */
export const buildCustomerData = (values: Record<string, string>): {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
} => {
  return {
    firstName: values.firstName || '',
    lastName: values.lastName || '',
    email: values.email || '',
    phone: values.phone || '',
  };
};

/**
 * Helper to find and update a customer field if it exists and is empty
 * Used during payment success to auto-fill customer fields from payment response
 * @param fields - All form fields
 * @param keys - Array of field keys to match (dataKey or label)
 * @param value - Value to set if field is found and empty
 * @param answers - Current answers map
 * @returns Updated answers map
 */
export const updateFieldIfMatch = (
  fields: Field[],
  keys: string[],
  value: string,
  answers: Map<string, any>
): Map<string, any> => {
  if (!value) return answers;

  const normalizedKeys = keys.map(k => k.toLowerCase());
  const field = fields.find(f => {
    // Check dataKey first (most reliable)
    if (f.dataKey && normalizedKeys.includes(f.dataKey.toLowerCase())) return true;
    // Fallback to label
    if (f.label && normalizedKeys.includes(f.label.toLowerCase())) return true;
    // Fallback to validation type for email fields
    if (normalizedKeys.includes('email') && f.validation?.type === 'email') return true;
    return false;
  });

  if (!field) return answers;

  // Check if field is currently empty in answers
  const currentValue = answers.get(field.id);
  if (!currentValue) {
    console.log(`✅ Auto-filling field "${field.label}" (${field.id}) with: "${value}"`);
    const updated = new Map(answers);
    updated.set(field.id, value);
    return updated;
  }

  console.log(`ℹ️ Field "${field.label}" (${field.id}) already has value: "${currentValue}", skipping auto-fill`);
  return answers;
};

/**
 * Auto-fill customer fields from payment response
 * @param fields - All form fields
 * @param customerData - Customer data from payment response
 * @param answers - Current answers map
 * @returns Updated answers map with auto-filled customer fields
 */
export const autoFillCustomerFields = (
  fields: Field[],
  customerData: { firstName: string; lastName: string; email: string; phone: string },
  answers: Map<string, any>
): Map<string, any> => {
  let updated = new Map(answers);

  console.log('🔄 Auto-filling customer fields from payment data:', customerData);

  // Attempt to map fields
  updated = updateFieldIfMatch(fields, ['first_name', 'firstname', 'name', 'details'], customerData.firstName, updated);
  updated = updateFieldIfMatch(fields, ['last_name', 'lastname', 'surname'], customerData.lastName, updated);
  updated = updateFieldIfMatch(fields, ['email', 'email_address', 'emailaddress'], customerData.email, updated);
  updated = updateFieldIfMatch(fields, ['phone', 'phone_number', 'phonenumber', 'mobile', 'cell', 'cellphone'], customerData.phone, updated);

  return updated;
};
